import Cookies from 'js-cookie';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    ReactNode,
} from 'react';
import { DatasetListItem } from 'types/Dataset';
import queryDatasetDetails from 'api/Dataset/queryDatasetDetails';

type RecentlyViewedContextType = {
    recentlyViewedUri: string[];
    addToRecentlyViewed: (newDatasetURI: string) => void;
    recentlyViewedDatasets: DatasetListItem[];
    loadingRecents: boolean;
};

type RecentlyViewedProviderProps = {
    children: ReactNode;
};

const RecentlyViewedContext = createContext<
    RecentlyViewedContextType | undefined
>(undefined);

export const RecentlyViewedProvider: React.FC<RecentlyViewedProviderProps> = ({
    children,
}) => {
    const client = useClient();
    const { openAlert } = useAlert();

    const [loadingRecents, setLoadingRecents] = useState<boolean>(true);
    const [recentlyViewedUri, setRecentlyViewedUri] = useState<string[]>([]);
    const [recentlyViewedDatasets, setRecentlyViewedUriDatasets] = useState<
        DatasetListItem[]
    >([]);

    useEffect(() => {
        const cookieData = Cookies.get(`recentlyViewed`);
        setRecentlyViewedUri(cookieData ? JSON.parse(cookieData) : []);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const responses = await Promise.all(
                    recentlyViewedUri.map((uri) =>
                        client?.query(queryDatasetDetails(uri)),
                    ),
                );
                const recentlyViewedDatasets = responses
                    .filter((response) => response.data.getDataset !== null)
                    .map((response) => response.data.getDataset);

                setRecentlyViewedUriDatasets(recentlyViewedDatasets);
            } catch (error) {
                openAlert({
                    message:
                        'Error fetching recently viewed recentlyViewedDatasets',
                    severity: 'alert',
                });
            } finally {
                setLoadingRecents(false);
            }
        };

        if (recentlyViewedUri.length > 0 && client) {
            fetchData();
        }
    }, [client, recentlyViewedUri, openAlert]);

    useEffect(() => {
        Cookies.set(`recentlyViewed`, JSON.stringify(recentlyViewedUri), {
            expires: 30,
        });
    }, [recentlyViewedUri]);

    const addToRecentlyViewed = (newDatasetURI: string) => {
        const updatedRecentlyViewed = recentlyViewedUri.filter(
            (existingItem) => existingItem !== newDatasetURI,
        );
        updatedRecentlyViewed.unshift(newDatasetURI);
        updatedRecentlyViewed.splice(4);
        setRecentlyViewedUri(updatedRecentlyViewed);
    };

    return (
        <RecentlyViewedContext.Provider
            value={{
                recentlyViewedUri,
                addToRecentlyViewed,
                recentlyViewedDatasets,
                loadingRecents,
            }}
        >
            {children}
        </RecentlyViewedContext.Provider>
    );
};

export const useRecentlyViewed = (): RecentlyViewedContextType => {
    const context = useContext(RecentlyViewedContext);
    if (!context) {
        throw new Error(
            'useRecentlyViewed must be used within a RecentlyViewedProvider',
        );
    }
    return context;
};
